import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { ptBR } from "date-fns/locale";

import { MdCalendarMonth, MdWorkOutline, MdErrorOutline, MdMailOutline, MdChat } from "react-icons/md";

import DatePicker from "react-datepicker";
import { toast } from "react-toastify";

import api from "../../../../services/axios";
import { Company } from "../../../../interfaces/Company";

type BillingType = "fixed" | "hourly" | "recurring" | "";

type ProjectModel = {
  projectName: string;
  description: string;
  clientId: number;
  startDate: Date | undefined;
  estimatedEndDate: Date | undefined;
  billingType: number | null;
  amount: number | null;
  billingChannels: number[];
};

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";
const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

const ModalAddProject: React.FC<any> = ({
  openAddProjectModal,
  setOpenAddProjectModal,
  details,
  updateState,
}) => {
  const [clientList, setClientList] = useState<Company[]>([]);
  const [selectedClient, setSelectedClient] = useState<number>(0);
  const [nameProject, setNameProject] = useState<string>("");
  const [projectStartDate, setProjectStartDate] = useState<Date>();
  const [projectEndDate, setProjectEndDate] = useState<Date>();
  const [billingType, setBillingType] = useState<BillingType>("");
  const [billingAmount, setBillingAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [billingEmail, setBillingEmail] = useState<boolean>(false);
  const [billingWhatsapp, setBillingWhatsapp] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpenAddProjectModal(false);
  };

  const handleRegisterProject = async () => {
    const billingTypeMap: Record<NonNullable<Exclude<BillingType, "">>, number> = {
      fixed: 1,
      hourly: 2,
      recurring: 3,
    };
    const billingChannels: number[] = [];
    if (billingEmail) billingChannels.push(1);
    if (billingWhatsapp) billingChannels.push(2);

    const projectData = {
      projectName: nameProject,
      description: description,
      clientId: selectedClient,
      startDate: projectStartDate,
      estimatedEndDate: projectEndDate,
      billingType: billingType ? billingTypeMap[billingType] : null,
      amount: billingAmount !== "" ? parseFloat(billingAmount) : null,
      billingChannels: billingChannels,
    };
    addProject(projectData);
    handleClose();
  };

  const handleProjectStartDatePicker = (date) => {
    const newData = new Date(date);
    setProjectStartDate(newData);
  };
  const handleProjectEndDatePicker = (date) => {
    const newData = new Date(date);
    setProjectEndDate(newData);
  };

  async function addProject(project: ProjectModel) {
    try {
      setLoading(true);
      const response = await api.post("/project", project);

      if (response.status !== 200) {
        let errorMessage =
          response.data.message || JSON.stringify(response.data);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
      setLoading(false);
      toast.success("Success to add Project", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      updateState();
      return response.data;
    } catch (error) {
      console.error("Error adding project:", error);
      setErrorMessage("Error adding project:");
      setLoading(false);
      toast.error("NetWork Error to adding project", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }

  useEffect(() => {
    setLoading(true);
    function fetchClients() {
      api
        .get("/Client/Clients")
        .then((response) => {
          setClientList((response.data ?? []).filter((c: any) => c.flActive));
          setLoading(false);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setLoading(true);
        });
    }
    fetchClients();
  }, []);

  const handleClientChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClient(parseInt(event.target.value));
  };

  const billingAmountLabel: Record<NonNullable<Exclude<BillingType, "">>, string> = {
    fixed: "Valor fixo",
    hourly: "Valor por hora",
    recurring: "Valor mensal",
  };

  return (
    <>
      <Modal
        backdrop={"static"}
        open={openAddProjectModal}
        size={"lg"}
        onClose={handleClose}
      >
        <Modal.Header className="border-b border-gray-100 pb-3 dark:border-gray-700">
          <Modal.Title>
            <div className="flex items-center gap-3">
              <div className="dv-aura flex h-11 w-11 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
                <MdWorkOutline className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-navy-700 dark:text-white">
                  Adicionar novo projeto
                </h2>
                <p className="text-xs font-normal text-gray-700 dark:text-gray-300">
                  Preencha as informações abaixo para cadastrar um projeto
                </p>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="flex flex-col gap-4 px-1 py-4">

            {/* Row 1 – Client & Project Name */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-client" className={labelCls}>
                  Cliente <span className="text-red-500">*</span>
                </label>
                <select
                  id="add-project-client"
                  onChange={handleClientChange}
                  value={selectedClient}
                  className={inputCls}
                >
                  <option value={0}>Selecione um cliente</option>
                  {clientList.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.legalName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-name" className={labelCls}>
                  Nome do projeto <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-project-name"
                  value={nameProject}
                  onChange={(e) => setNameProject(e.target.value)}
                  className={inputCls}
                  placeholder="ex.: Redesign do site"
                  required
                />
              </div>
            </div>

            {/* Row 2 – Dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-startDate" className={labelCls}>
                  Data de início <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  id="add-project-startDate"
                  showIcon
                  selected={projectStartDate}
                  locale={ptBR}
                  icon={<MdCalendarMonth className="mt-1.5 text-base" aria-hidden="true" />}
                  dateFormat="dd-MM-yyyy"
                  onChange={(date) => handleProjectStartDatePicker(date)}
                  placeholderText="Selecione a data de início"
                  className="h-[42px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-endDate" className={labelCls}>
                  Término previsto <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  id="add-project-endDate"
                  showIcon
                  selected={projectEndDate}
                  locale={ptBR}
                  icon={<MdCalendarMonth className="mt-1.5 text-base" aria-hidden="true" />}
                  dateFormat="dd-MM-yyyy"
                  onChange={(date) => handleProjectEndDatePicker(date)}
                  placeholderText="Selecione a data de término"
                  className="h-[42px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="add-project-description" className={labelCls}>
                Descrição
              </label>
              <textarea
                id="add-project-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Breve descrição do projeto (opcional)"
                className={`${inputCls} resize-none`}
              />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <hr className="flex-1 border-gray-100 dark:border-gray-700" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Faturamento
              </span>
              <hr className="flex-1 border-gray-100 dark:border-gray-700" />
            </div>

            {/* Row 3 – Billing Type & Amount */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-billingType" className={labelCls}>
                  Tipo de faturamento
                </label>
                <select
                  id="add-project-billingType"
                  value={billingType}
                  onChange={(e) => setBillingType(e.target.value as BillingType)}
                  className={inputCls}
                >
                  <option value="">Selecione o tipo</option>
                  <option value="fixed">Fixed Price</option>
                  <option value="hourly">Hourly Rate</option>
                  <option value="recurring">Recurring</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-project-amount" className={labelCls}>
                  {billingType ? billingAmountLabel[billingType] : "Valor"}
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-400" aria-hidden="true">
                    R$
                  </span>
                  <input
                    id="add-project-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={billingAmount}
                    onChange={(e) => setBillingAmount(e.target.value)}
                    disabled={!billingType}
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                    placeholder={billingType === "hourly" ? "0.00 / hr" : billingType === "recurring" ? "0.00 / mês" : "0.00"}
                  />
                </div>
              </div>
            </div>

            {/* Row 4 – Billing Channel */}
            <div className="flex flex-col gap-2">
              <span className={labelCls}>Canal de faturamento</span>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${billingEmail ? "border-dv-blue-500 bg-dv-blue-500/10 text-dv-blue-600 dark:text-dv-cyan-300" : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:text-gray-300"}`}>
                  <input
                    type="checkbox"
                    checked={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.checked)}
                    className="h-4 w-4 rounded accent-dv-blue-500"
                  />
                  <MdMailOutline className="h-4 w-4" aria-hidden="true" />
                  E-mail
                </label>
                <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${billingWhatsapp ? "border-dv-blue-500 bg-dv-blue-500/10 text-dv-blue-600 dark:text-dv-cyan-300" : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:text-gray-300"}`}>
                  <input
                    type="checkbox"
                    checked={billingWhatsapp}
                    onChange={(e) => setBillingWhatsapp(e.target.checked)}
                    className="h-4 w-4 rounded accent-dv-blue-500"
                  />
                  <MdChat className="h-4 w-4" aria-hidden="true" />
                  WhatsApp
                </label>
              </div>
            </div>

            {errorMessage && (
              <div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
                <MdErrorOutline className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {errorMessage}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-t border-gray-100 pt-3 dark:border-gray-700">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            {loading ? (
              <button
                type="button"
                className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white opacity-60 dark:bg-white dark:text-ink-900"
                disabled
              >
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processando…
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleRegisterProject}
                disabled={
                  nameProject.length < 5 ||
                  selectedClient === 0 ||
                  projectStartDate == null ||
                  projectEndDate == null
                }
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                Confirmar
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ModalAddProject;
